import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtTime = () => new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const fmtDate = () => new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const STEP_LANDING    = 'landing';
const STEP_DETAILS    = 'details';
const STEP_HOST       = 'host';
const STEP_REASON     = 'reason';
const STEP_MEDIA      = 'media';
const STEP_CONFIRM    = 'confirm';
const STEP_WELCOME    = 'welcome';
const STEP_SIGNOUT_SEARCH = 'signout_search';
const STEP_SIGNOUT_CONFIRM = 'signout_confirm';
const STEP_GOODBYE    = 'goodbye';
const STEP_BLOCKED    = 'blocked';

export default function KioskPage({ match, params }) {
  // Get premisesId from URL
  const pathParts = window.location.pathname.split('/');
  const tenantSlug = pathParts[2];
  const premisesSlug = pathParts[3];

  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [step, setStep] = useState(STEP_LANDING);
  const [time, setTime] = useState(fmtTime());
  const [resetTimer, setResetTimer] = useState(null);
  const [error, setError] = useState(null);

  // Form state
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', reason: '' });
  const [selectedHost, setSelectedHost] = useState(null);
  const [hostSearch, setHostSearch] = useState('');
  const [matchedPreReg, setMatchedPreReg] = useState(null);
  const [matchedAV, setMatchedAV] = useState(null);
  const [photoFileId, setPhotoFileId] = useState(null);
  const [videoFileId, setVideoFileId] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [welcomeData, setWelcomeData] = useState(null);
  const [signOutVisitors, setSignOutVisitors] = useState([]);
  const [signOutSearch, setSignOutSearch] = useState('');
  const [selectedSignOut, setSelectedSignOut] = useState(null);
  const [blockedMessage, setBlockedMessage] = useState('');

  // Camera state
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Resolve premises by slug
  const [premisesId, setPremisesId] = useState(null);

  useEffect(() => {
    const tick = setInterval(() => setTime(fmtTime()), 10000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    // Find premises by tenant slug + premises slug
    api.get(`/visitors/kiosk/by-slug/${tenantSlug}/${premisesSlug}`)
      .then(r => {
        setPremisesId(r.data.premises.id);
        setConfig(r.data);
      })
      .catch(e => setError('Kiosk not found. Please check the URL.'));
  }, [tenantSlug, premisesSlug]);

  useEffect(() => {
    if (!premisesId) return;
    api.get(`/visitors/kiosk/${premisesId}/users`).then(r => setUsers(r.data)).catch(() => {});
  }, [premisesId]);

  // Auto-reset on inactivity
  const scheduleReset = useCallback(() => {
    if (resetTimer) clearTimeout(resetTimer);
    const t = setTimeout(() => goToLanding(), 60000);
    setResetTimer(t);
  }, [resetTimer]);

  const goToLanding = useCallback(() => {
    stopCamera();
    setStep(STEP_LANDING);
    setForm({ name: '', company: '', email: '', phone: '', reason: '' });
    setSelectedHost(null); setHostSearch(''); setMatchedPreReg(null); setMatchedAV(null);
    setPhotoFileId(null); setVideoFileId(null); setPhotoPreview(null); setWelcomeData(null);
    setSignOutSearch(''); setSignOutVisitors([]); setSelectedSignOut(null);
    if (resetTimer) clearTimeout(resetTimer);
  }, [resetTimer]);

  // Camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 1280, height: 720 }, audio: config?.premises?.feature_video });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
      setCameraReady(true);
    } catch(e) { console.error('Camera error:', e); }
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setCameraReady(false);
  };

  const takePhoto = async () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 1280;
    canvas.height = videoRef.current.videoHeight || 720;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    const preview = canvas.toDataURL('image/jpeg', 0.85);
    setPhotoPreview(preview);
    canvas.toBlob(async (blob) => {
      const fd = new FormData(); fd.append('file', blob, 'visitor-photo.jpg');
      fd.append('premises_id', premisesId);
      const { data } = await api.post('/visitors/media/photo', fd);
      setPhotoFileId(data.file_id);
    }, 'image/jpeg', 0.85);
  };

  const startVideo = () => {
    if (!streamRef.current) return;
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' : 'video/mp4';
    chunksRef.current = [];
    const rec = new MediaRecorder(streamRef.current, { mimeType });
    rec.ondataavailable = e => chunksRef.current.push(e.data);
    rec.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const fd = new FormData(); fd.append('file', blob, 'visitor-video.webm');
      fd.append('premises_id', premisesId);
      const { data } = await api.post('/visitors/media/video', fd);
      setVideoFileId(data.file_id);
      setRecording(false);
    };
    recorderRef.current = rec;
    rec.start();
    setRecording(true);
    setRecordingSeconds(15);
    const countdown = setInterval(() => {
      setRecordingSeconds(s => {
        if (s <= 1) { clearInterval(countdown); rec.stop(); return 0; }
        return s - 1;
      });
    }, 1000);
    setTimeout(() => { if (rec.state === 'recording') rec.stop(); }, 15000);
  };

  const stopVideo = () => { if (recorderRef.current?.state === 'recording') recorderRef.current.stop(); };

  // Match pre-registration by email
  const checkMatch = async (email, name) => {
    if (!email && !name) return;
    try {
      const { data } = await api.post(`/visitors/kiosk/${premisesId}/match`, { email, name });
      if (data.match) {
        if (data.type === 'pre_registration') {
          setMatchedPreReg(data.match);
          setForm(f => ({
            ...f,
            name: data.match.visitor_name || f.name,
            company: data.match.visitor_company || f.company,
            email: data.match.visitor_email || f.email,
            phone: data.match.visitor_phone || f.phone,
            reason: data.match.visit_reason || f.reason,
          }));
          if (data.match.visited_user_id) {
            const host = users.find(u => u.id === data.match.visited_user_id || String(u.id) === String(data.match.visited_user_id));
            if (host) setSelectedHost(host);
          }
        } else if (data.type === 'authorised_visit') {
          setMatchedAV(data.match);
        }
      }
    } catch(e) {}
  };

  const submitSignIn = async () => {
    try {
      const payload = {
        premises_id: premisesId,
        visitor_name: form.name,
        visitor_company: form.company || null,
        visitor_email: form.email || null,
        visitor_phone: form.phone || null,
        visited_user_id: selectedHost?.id,
        visit_reason: form.reason || null,
        avatar_file_id: photoFileId || null,
        video_file_id: videoFileId || null,
        pre_registration_id: matchedPreReg?.id || null,
        authorised_visit_id: matchedAV?.id || null,
      };
      const { data } = await api.post('/visitors/sign-in', payload);
      stopCamera();
      setWelcomeData(data);
      setStep(STEP_WELCOME);
      setTimeout(goToLanding, 12000);
    } catch(e) {
      if (e.response?.data?.error === 'no_planned_visit') {
        setBlockedMessage(e.response.data.message);
        setStep(STEP_BLOCKED);
      }
    }
  };

  const searchSignOut = async () => {
    if (!signOutSearch) return;
    const { data } = await api.get(`/visitors/kiosk/${premisesId}/active?search=${encodeURIComponent(signOutSearch)}`);
    setSignOutVisitors(data);
  };

  const submitSignOut = async () => {
    if (!selectedSignOut) return;
    await api.put(`/visitors/${selectedSignOut.id}/sign-out`, {});
    setStep(STEP_GOODBYE);
    setTimeout(goToLanding, 6000);
  };

  if (error) return (
    <div style={styles.page}>
      <div style={styles.errorBox}><span style={{ fontSize: 48 }}>⚠️</span><p style={{ marginTop: 16, fontSize: 20 }}>{error}</p></div>
    </div>
  );

  if (!config) return (
    <div style={styles.page}><div style={{ color: 'white', fontSize: 24 }}>Loading...</div></div>
  );

  const { premises, company } = config;
  const needsMedia = premises.feature_photo || premises.feature_video;
  const steps = [STEP_DETAILS, STEP_HOST, ...(premises.feature_show_reason ? [STEP_REASON] : []), ...(needsMedia ? [STEP_MEDIA] : [])];
  const currentStepIndex = steps.indexOf(step);
  const totalSteps = steps.length;

  const filteredUsers = users.filter(u => {
    if (!hostSearch) return true;
    return (u.display_name || u.username || '').toLowerCase().includes(hostSearch.toLowerCase());
  });

  return (
    <div style={styles.page} onClick={scheduleReset}>
      {/* Header bar */}
      <div style={styles.topBar}>
        <span style={styles.timeText}>{time}</span>
        <span style={styles.dateText}>{fmtDate()}</span>
      </div>

      {/* LANDING */}
      {step === STEP_LANDING && (
        <div style={styles.center}>
          <div style={styles.logoArea}>
            {company.logo_url
              ? <img src={`/api/files/${company.logo_url}`} alt={company.name} style={styles.logo} />
              : <div style={styles.companyNameLarge}>{company.name}</div>}
            {company.logo_url && <div style={styles.companySubtitle}>{company.name}</div>}
          </div>
          {premises.welcome_message && <p style={styles.welcomeMsg}>{premises.welcome_message}</p>}
          <button style={styles.btnPrimary} onClick={() => { setStep(STEP_DETAILS); scheduleReset(); }}>
            👋&nbsp;&nbsp;Sign In
          </button>
          <button style={styles.btnSecondary} onClick={() => { setStep(STEP_SIGNOUT_SEARCH); scheduleReset(); }}>
            👋&nbsp;&nbsp;Sign Out
          </button>
          {premises.feature_pre_reg_landing && (
            <div style={styles.preRegHint} onClick={() => { setStep(STEP_DETAILS); scheduleReset(); }}>
              Are you expected today? Tap Sign In →
            </div>
          )}
        </div>
      )}

      {/* DETAILS */}
      {step === STEP_DETAILS && (
        <div style={styles.formCard}>
          <StepHeader back={goToLanding} title="Your Details" step={1} total={totalSteps} />
          {matchedPreReg && <MatchBanner name={matchedPreReg.visitor_name} type="pre-registration" />}
          {matchedAV && <MatchBanner name={matchedAV.title} type="planned-visit" />}
          <Field label="Full Name *" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} onBlur={() => checkMatch(form.email, form.name)} />
          <Field label="Company" value={form.company} onChange={v => setForm(f => ({ ...f, company: v }))} />
          <Field label={`Email${premises.feature_require_email ? ' *' : ''}`} value={form.email} type="email"
            onChange={v => setForm(f => ({ ...f, email: v }))} onBlur={() => checkMatch(form.email, form.name)} />
          <Field label="Phone" value={form.phone} type="tel" onChange={v => setForm(f => ({ ...f, phone: v }))} />
          <ContinueBtn disabled={!form.name || (premises.feature_require_email && !form.email)}
            onClick={() => setStep(selectedHost ? (premises.feature_show_reason ? STEP_REASON : (needsMedia ? STEP_MEDIA : STEP_CONFIRM)) : STEP_HOST)} />
        </div>
      )}

      {/* HOST SELECTION */}
      {step === STEP_HOST && (
        <div style={styles.formCard}>
          <StepHeader back={() => setStep(STEP_DETAILS)} title="Who are you visiting?" step={2} total={totalSteps} />
          <input style={styles.searchInput} placeholder="Search by name..." value={hostSearch}
            onChange={e => setHostSearch(e.target.value)} autoFocus />
          <div style={styles.userList}>
            {filteredUsers.map(u => (
              <div key={u.id} style={{ ...styles.userRow, background: selectedHost?.id === u.id ? '#EE720320' : '#fff', borderColor: selectedHost?.id === u.id ? '#EE7203' : '#e2e8f0' }}
                onClick={() => setSelectedHost(u)}>
                <div style={styles.userAvatar}>
                  {u.avatar_url ? <img src={`/api/files/${u.avatar_url}`} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} /> : <span style={{ fontSize: 22 }}>👤</span>}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#1a202c' }}>{u.display_name || u.username}</div>
                </div>
                {selectedHost?.id === u.id && <span style={{ marginLeft: 'auto', color: '#EE7203', fontSize: 22 }}>✓</span>}
              </div>
            ))}
          </div>
          <ContinueBtn disabled={!selectedHost}
            onClick={() => setStep(premises.feature_show_reason ? STEP_REASON : (needsMedia ? STEP_MEDIA : STEP_CONFIRM))} />
        </div>
      )}

      {/* REASON */}
      {step === STEP_REASON && premises.feature_show_reason && (
        <div style={styles.formCard}>
          <StepHeader back={() => setStep(STEP_HOST)} title="Your Visit" step={currentStepIndex + 1} total={totalSteps} />
          <label style={styles.fieldLabel}>Purpose of your visit</label>
          <textarea style={styles.textarea} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={5} placeholder="Optional — briefly describe the purpose of your visit..." />
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button style={styles.btnGhost} onClick={() => setStep(needsMedia ? STEP_MEDIA : STEP_CONFIRM)}>Skip</button>
            <ContinueBtn onClick={() => setStep(needsMedia ? STEP_MEDIA : STEP_CONFIRM)} />
          </div>
        </div>
      )}

      {/* MEDIA */}
      {step === STEP_MEDIA && needsMedia && (
        <div style={styles.formCard}>
          <StepHeader back={() => setStep(premises.feature_show_reason ? STEP_REASON : STEP_HOST)} title={premises.feature_video ? 'Photo & Video Sign-in' : 'Your Photo'} step={currentStepIndex + 1} total={totalSteps} />
          {!cameraReady && !photoPreview && (
            <button style={styles.btnPrimary} onClick={startCamera}>📷 Open Camera</button>
          )}
          {cameraReady && !photoPreview && (
            <div style={{ textAlign: 'center' }}>
              <video ref={videoRef} style={{ width: '100%', maxWidth: 480, borderRadius: 12, background: '#000' }} autoPlay muted playsInline />
              {recording && (
                <div style={{ background: '#e53e3e', color: '#fff', borderRadius: 8, padding: '8px 16px', margin: '8px auto', display: 'inline-block' }}>
                  🔴 Recording... {recordingSeconds}s remaining
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 12 }}>
                {premises.feature_photo && !recording && (
                  <button style={styles.btnPrimary} onClick={takePhoto}>📷 Take Photo</button>
                )}
                {premises.feature_video && !recording && !videoFileId && (
                  <button style={{ ...styles.btnPrimary, background: '#e53e3e' }} onClick={startVideo}>🎥 Start Video ({`max 15s`})</button>
                )}
                {recording && (
                  <button style={styles.btnGhost} onClick={stopVideo}>Stop Early</button>
                )}
              </div>
              {premises.feature_video && <p style={{ fontSize: 14, color: '#718096', marginTop: 8 }}>Please say your name and who you are here to see.</p>}
            </div>
          )}
          {photoPreview && (
            <div style={{ textAlign: 'center' }}>
              <img src={photoPreview} alt="Preview" style={{ width: 220, height: 220, objectFit: 'cover', borderRadius: '50%', border: '4px solid #EE7203' }} />
              {videoFileId && <div style={{ color: '#38a169', marginTop: 8 }}>✓ Video recorded</div>}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 16 }}>
                <button style={styles.btnGhost} onClick={() => { setPhotoPreview(null); setPhotoFileId(null); }}>Retake</button>
                <ContinueBtn label="Use This →" onClick={() => setStep(STEP_CONFIRM)} />
              </div>
            </div>
          )}
          {!needsMedia || (!premises.feature_photo && premises.feature_video && videoFileId) && (
            <ContinueBtn onClick={() => setStep(STEP_CONFIRM)} />
          )}
          <button style={{ ...styles.btnGhost, marginTop: 16 }} onClick={() => setStep(STEP_CONFIRM)}>Skip</button>
        </div>
      )}

      {/* CONFIRM */}
      {step === STEP_CONFIRM && (
        <div style={styles.formCard}>
          <StepHeader back={() => setStep(needsMedia ? STEP_MEDIA : (premises.feature_show_reason ? STEP_REASON : STEP_HOST))} title="Confirm Details" step={totalSteps} total={totalSteps} />
          <div style={styles.summaryBox}>
            {photoPreview && <img src={photoPreview} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #EE7203', marginBottom: 12 }} />}
            <SummaryRow label="Name" value={form.name} />
            {form.company && <SummaryRow label="Company" value={form.company} />}
            {form.email && <SummaryRow label="Email" value={form.email} />}
            {form.phone && <SummaryRow label="Phone" value={form.phone} />}
            <SummaryRow label="Visiting" value={selectedHost?.display_name || selectedHost?.username} />
            {form.reason && <SummaryRow label="Reason" value={form.reason} />}
          </div>
          <button style={styles.btnPrimary} onClick={submitSignIn}>✓ Complete Sign In</button>
        </div>
      )}

      {/* BLOCKED */}
      {step === STEP_BLOCKED && (
        <div style={styles.center}>
          <div style={styles.blockedBox}>
            <div style={{ fontSize: 56 }}>🔒</div>
            <h2 style={{ fontSize: 28, fontWeight: 800, margin: '16px 0 8px', color: '#1a202c' }}>Access Not Authorised</h2>
            <p style={{ fontSize: 18, color: '#718096', marginBottom: 32 }}>{blockedMessage}</p>
            <button style={styles.btnGhost} onClick={goToLanding}>← Return to Start</button>
          </div>
        </div>
      )}

      {/* WELCOME */}
      {step === STEP_WELCOME && welcomeData && (
        <div style={styles.center}>
          <div style={styles.welcomeBox}>
            {photoPreview && <img src={photoPreview} alt="" style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '4px solid #EE7203', margin: '0 auto 16px' }} />}
            <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
            <h1 style={{ fontSize: 36, fontWeight: 800, color: '#1a202c', margin: '8px 0' }}>Welcome, {form.name}!</h1>
            <p style={{ fontSize: 18, color: '#4a5568', marginBottom: 8 }}>
              <strong>{selectedHost?.display_name}</strong> has been notified of your arrival.
            </p>
            {welcomeData.badge_number && (
              <div style={styles.badgeCard}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#718096', marginBottom: 4 }}>VISITOR PASS</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{form.name}</div>
                {form.company && <div style={{ fontSize: 16, color: '#4a5568' }}>{form.company}</div>}
                <div style={{ fontSize: 14, color: '#718096', marginTop: 4 }}>Visiting: {selectedHost?.display_name}</div>
                <div style={{ fontSize: 12, color: '#a0aec0', marginTop: 4 }}>{new Date().toLocaleString('en-GB')}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: '#EE7203', marginTop: 8 }}>{welcomeData.badge_number}</div>
              </div>
            )}
            {premises.feature_badge_print && <button style={{ ...styles.btnGhost, marginTop: 12 }} onClick={() => window.print()}>🖨️ Print Pass</button>}
            <p style={{ fontSize: 14, color: '#a0aec0', marginTop: 20 }}>Returning to start shortly...</p>
          </div>
        </div>
      )}

      {/* SIGN OUT SEARCH */}
      {step === STEP_SIGNOUT_SEARCH && (
        <div style={styles.formCard}>
          <StepHeader back={goToLanding} title="Sign Out" />
          <p style={{ fontSize: 16, color: '#718096', marginBottom: 16 }}>Enter your name or email to find your record.</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <input style={{ ...styles.fieldInput, flex: 1 }} placeholder="Your name or email..." value={signOutSearch}
              onChange={e => setSignOutSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchSignOut()} autoFocus />
            <button style={styles.btnPrimary} onClick={searchSignOut}>Search</button>
          </div>
          <div style={{ marginTop: 20 }}>
            {signOutVisitors.map(v => (
              <div key={v.id} style={{ ...styles.userRow, cursor: 'pointer', background: selectedSignOut?.id === v.id ? '#EE720310' : '#fff', borderColor: selectedSignOut?.id === v.id ? '#EE7203' : '#e2e8f0' }}
                onClick={() => setSelectedSignOut(v)}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{v.visitor_name}</div>
                  {v.visitor_company && <div style={{ fontSize: 13, color: '#718096' }}>{v.visitor_company}</div>}
                  <div style={{ fontSize: 13, color: '#a0aec0' }}>Visiting {v.host_name} · since {new Date(v.sign_in_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                {selectedSignOut?.id === v.id && <span style={{ marginLeft: 'auto', color: '#EE7203', fontSize: 22 }}>✓</span>}
              </div>
            ))}
            {signOutVisitors.length === 0 && signOutSearch && <p style={{ color: '#a0aec0' }}>No matching visitors found.</p>}
          </div>
          {selectedSignOut && <ContinueBtn label="Confirm Sign Out →" onClick={() => setStep(STEP_SIGNOUT_CONFIRM)} />}
        </div>
      )}

      {/* SIGN OUT CONFIRM */}
      {step === STEP_SIGNOUT_CONFIRM && selectedSignOut && (
        <div style={styles.formCard}>
          <StepHeader back={() => setStep(STEP_SIGNOUT_SEARCH)} title="Confirm Sign Out" />
          <div style={styles.summaryBox}>
            <SummaryRow label="Name" value={selectedSignOut.visitor_name} />
            {selectedSignOut.visitor_company && <SummaryRow label="Company" value={selectedSignOut.visitor_company} />}
            <SummaryRow label="Visiting" value={selectedSignOut.host_name} />
            <SummaryRow label="Signed in" value={new Date(selectedSignOut.sign_in_time).toLocaleString('en-GB')} />
          </div>
          <button style={{ ...styles.btnPrimary, background: '#276749' }} onClick={submitSignOut}>✓ Sign Out</button>
        </div>
      )}

      {/* GOODBYE */}
      {step === STEP_GOODBYE && (
        <div style={styles.center}>
          <div style={styles.welcomeBox}>
            <div style={{ fontSize: 56 }}>👋</div>
            <h1 style={{ fontSize: 36, fontWeight: 800, color: '#1a202c', margin: '12px 0 8px' }}>Goodbye, {selectedSignOut?.visitor_name}!</h1>
            <p style={{ fontSize: 20, color: '#4a5568' }}>Thank you for visiting.</p>
            <p style={{ fontSize: 14, color: '#a0aec0', marginTop: 20 }}>Returning to start shortly...</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StepHeader({ back, title, step, total }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 12 }}>
      <button style={styles.backBtn} onClick={back}>← Back</button>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 800, fontSize: 22, color: '#1a202c' }}>{title}</div>
      </div>
      {step && <div style={{ fontSize: 13, color: '#a0aec0' }}>Step {step} of {total}</div>}
    </div>
  );
}

function Field({ label, value, onChange, onBlur, type = 'text' }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={styles.fieldLabel}>{label}</label>
      <input style={styles.fieldInput} type={type} value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur} />
    </div>
  );
}

function ContinueBtn({ onClick, disabled, label = 'Continue →' }) {
  return (
    <button style={{ ...styles.btnPrimary, opacity: disabled ? 0.4 : 1 }} onClick={onClick} disabled={disabled}>
      {label}
    </button>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
      <span style={{ width: 90, fontSize: 13, color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      <span style={{ fontSize: 15, color: '#1a202c', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function MatchBanner({ name, type }) {
  return (
    <div style={{ background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
      <span style={{ fontSize: 20 }}>✓</span>
      <div>
        <div style={{ fontWeight: 700, color: '#276749', fontSize: 14 }}>We were expecting you!</div>
        <div style={{ fontSize: 13, color: '#4a5568' }}>{type === 'planned-visit' ? `Planned visit: ${name}` : `Pre-registered: ${name}`}</div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 60%, #1a202c 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: 'Arial, sans-serif', userSelect: 'none' },
  topBar: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 32px', background: 'rgba(0,0,0,0.3)' },
  timeText: { fontSize: 28, fontWeight: 800, color: '#fff', letterSpacing: 2 },
  dateText: { fontSize: 15, color: '#a0aec0' },
  center: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: 32, width: '100%' },
  logoArea: { textAlign: 'center', marginBottom: 24 },
  logo: { maxHeight: 120, maxWidth: 320, objectFit: 'contain' },
  companyNameLarge: { fontSize: 48, fontWeight: 900, color: '#fff', letterSpacing: '-1px' },
  companySubtitle: { fontSize: 20, color: '#a0aec0', marginTop: 8 },
  welcomeMsg: { fontSize: 18, color: '#cbd5e0', textAlign: 'center', marginBottom: 32, maxWidth: 480 },
  btnPrimary: { display: 'block', width: '100%', maxWidth: 420, padding: '20px 32px', background: '#EE7203', color: '#fff', border: 'none', borderRadius: 14, fontSize: 22, fontWeight: 800, cursor: 'pointer', marginBottom: 16, letterSpacing: 0.5 },
  btnSecondary: { display: 'block', width: '100%', maxWidth: 420, padding: '20px 32px', background: 'transparent', color: '#fff', border: '2px solid rgba(255,255,255,0.3)', borderRadius: 14, fontSize: 22, fontWeight: 700, cursor: 'pointer', marginBottom: 16 },
  btnGhost: { padding: '12px 24px', background: 'transparent', color: '#718096', border: '1px solid #cbd5e0', borderRadius: 10, fontSize: 16, cursor: 'pointer' },
  preRegHint: { fontSize: 15, color: '#EE7203', cursor: 'pointer', marginTop: 8, textDecoration: 'underline' },
  formCard: { background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 560, margin: '24px auto', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' },
  backBtn: { background: 'none', border: 'none', color: '#718096', fontSize: 15, cursor: 'pointer', padding: '4px 8px' },
  fieldLabel: { display: 'block', fontSize: 13, fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 },
  fieldInput: { width: '100%', padding: '14px 16px', fontSize: 18, border: '2px solid #e2e8f0', borderRadius: 10, outline: 'none', boxSizing: 'border-box' },
  searchInput: { width: '100%', padding: '14px 16px', fontSize: 18, border: '2px solid #e2e8f0', borderRadius: 10, outline: 'none', marginBottom: 16, boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '14px 16px', fontSize: 16, border: '2px solid #e2e8f0', borderRadius: 10, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'Arial' },
  userList: { maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  userRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', border: '2px solid #e2e8f0', borderRadius: 12, cursor: 'pointer' },
  userAvatar: { width: 48, height: 48, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  summaryBox: { background: '#f7fafc', borderRadius: 12, padding: 20, marginBottom: 24 },
  welcomeBox: { background: '#fff', borderRadius: 20, padding: 40, textAlign: 'center', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' },
  badgeCard: { border: '2px dashed #EE7203', borderRadius: 12, padding: '16px 24px', margin: '16px auto', maxWidth: 300, textAlign: 'center', background: '#fff9f5' },
  blockedBox: { background: '#fff', borderRadius: 20, padding: 48, textAlign: 'center', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' },
  errorBox: { background: 'rgba(255,255,255,0.1)', borderRadius: 16, padding: 40, textAlign: 'center', color: '#fff' },
};
